import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Layout from './components/Layout';
import AccountManagement from './pages/AccountManagement';
import CustomerManagement from './pages/CustomerManagement';
import Notifications from './pages/Notifications';
import Reports from './pages/Reports';
import MasterPerformance from './pages/MasterPerformance';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return user ? children : <Navigate to="/login" />;
};

// Blocks non-admin/non-broker/non-client roles (e.g. viewer) from admin-only pages
const ViewerGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (user?.role === 'viewer') return <Navigate to="/" />;
  return children;
};

// Blocks viewer from tabs they're not allowed to access
const TabGuard = ({ tabKey, children }) => {
  const { user, loading, canAccessTab } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!canAccessTab(tabKey)) {
    // Redirect to the first allowed tab or login
    if (user?.role === 'viewer') {
      const tabs = user.allowed_tabs;
      if (tabs && tabs.includes('reports')) return <Navigate to="/reports" />;
      return <Navigate to="/" />;
    }
    return <Navigate to="/" />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<TabGuard tabKey="dashboard"><Dashboard /></TabGuard>} />
        <Route path="accounts" element={<ViewerGuard><AccountManagement /></ViewerGuard>} />
        <Route path="customers" element={<ViewerGuard><CustomerManagement /></ViewerGuard>} />
        <Route path="notifications" element={<ViewerGuard><Notifications /></ViewerGuard>} />
        <Route path="reports" element={<TabGuard tabKey="reports"><Reports /></TabGuard>} />
        <Route path="performance" element={<TabGuard tabKey="master-performance"><MasterPerformance /></TabGuard>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <AppRoutes />
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
